import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, BookOpen, FileText, PlayCircle } from "lucide-react";

const RESOURCES = [
  {
    tag: "Whitepaper",
    icon: FileText,
    title: "The AI-Enabled Clinical Governance Playbook",
    excerpt:
      "How healthcare leaders are using embedded AI to cut audit resource requirements by 30% — without adding headcount.",
    read: "12 min read",
  },
  {
    tag: "Guide",
    icon: BookOpen,
    title: "From Spreadsheets to Assurance at Scale",
    excerpt:
      "A step-by-step framework for moving away from manual audit and towards population-based, API-driven assurance.",
    read: "9 min read",
  },
  {
    tag: "Webinar",
    icon: PlayCircle,
    title: "Getting Ready for NSQHS with QC4Health",
    excerpt:
      "How to build accreditation-ready evidence packs continuously — instead of scrambling in the weeks before survey.",
    read: "45 min watch",
  },
  {
    tag: "Article",
    icon: FileText,
    title: "Building a Risk-Aware Culture Across Every Ward",
    excerpt:
      "Standardised risk language, crowd-sourced identification and clear KRIs — what actually works in large services.",
    read: "7 min read",
  },
  {
    tag: "Case Study",
    icon: BookOpen,
    title: "How a Regional Network Cut Compliance Workload by 40%",
    excerpt:
      "A multi-site case study showing the operational and clinical impact of integrated governance.",
    read: "6 min read",
  },
  {
    tag: "Article",
    icon: FileText,
    title: "Credentials Management, Automated",
    excerpt:
      "Automatic AHPRA sync, continuous monitoring and smart renewal reminders — without spreadsheets or chase-ups.",
    read: "5 min read",
  },
];

export default function Resources() {
  return (
    <div data-testid="resources-page">
      {/* HERO */}
      <section className="relative overflow-hidden bg-white border-b border-slate-200">
        <div className="absolute inset-0 bg-dots opacity-70 pointer-events-none" />
        <div className="relative max-w-4xl mx-auto px-5 sm:px-8 py-20 md:py-28 text-center">
          <div className="text-xs font-bold tracking-[0.2em] uppercase text-[#22A34A] mb-4">
            Resources
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-slate-900 leading-[1.05]">
            Ideas, guides and case studies for clinical governance leaders.
          </h1>
          <p className="mt-6 text-lg text-slate-600 leading-relaxed">
            Practical thinking from the QC4Health team and the hospitals we
            work with. Written for the people running risk, quality and audit.
          </p>
        </div>
      </section>

      {/* GRID */}
      <section className="py-20 md:py-24">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {RESOURCES.map((r, i) => {
              const Icon = r.icon;
              return (
                <article
                  key={i}
                  className="group bg-white rounded-2xl border border-slate-100 shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all overflow-hidden"
                  data-testid={`resource-card-${i}`}
                >
                  <div className="aspect-[16/9] bg-gradient-to-br from-slate-50 to-slate-100 relative overflow-hidden flex items-center justify-center border-b border-slate-100">
                    <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-[#22A34A]/10 blur-2xl" />
                    <div className="absolute -bottom-10 -left-10 w-40 h-40 rounded-full bg-[#3FB8E5]/10 blur-2xl" />
                    <div className="relative w-16 h-16 rounded-2xl bg-white shadow border border-slate-100 flex items-center justify-center">
                      <Icon className="w-6 h-6 text-[#22A34A]" />
                    </div>
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-3 mb-3">
                      <span className="text-[11px] font-bold tracking-[0.18em] uppercase text-[#22A34A]">
                        {r.tag}
                      </span>
                      <span className="w-1 h-1 rounded-full bg-slate-300" />
                      <span className="text-xs text-slate-500">{r.read}</span>
                    </div>
                    <h3 className="font-heading text-lg font-semibold text-slate-900 group-hover:text-[#22A34A] transition-colors">
                      {r.title}
                    </h3>
                    <p className="mt-3 text-sm text-slate-600 leading-relaxed">
                      {r.excerpt}
                    </p>
                    <div className="mt-5 inline-flex items-center gap-2 text-sm font-medium text-[#22A34A] group-hover:gap-3 transition-all">
                      Read more
                      <ArrowRight className="w-4 h-4" />
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-white border-t border-slate-200">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-semibold text-slate-900 tracking-tight">
            Prefer a conversation to a whitepaper?
          </h2>
          <p className="mt-4 text-lg text-slate-600">
            Book a 60-minute walkthrough and we&apos;ll answer your questions
            live.
          </p>
          <Link
            to="/book-demo"
            data-testid="resources-cta-book-demo-btn"
            className="mt-8 inline-flex items-center gap-2 bg-[#22A34A] hover:bg-[#1e9042] text-white rounded-full px-8 py-3.5 font-medium transition-all hover:-translate-y-0.5"
          >
            Book a Demo
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
